import { useEffect, useState } from 'react';
import type { WorkflowTriggerNotification, OrderWorkflowTriggerNotification } from '../types/connector.types';
import type { UseNotificationsResult } from '../hooks/useNotifications';
import { humaniseWorkflow, humaniseOrder } from '../hooks/useNotifications';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Workflow trigger config ──────────────────────────────────────────────────

interface TriggerConfig {
  color: string;
  bg: string;
  icon: React.ReactNode;
  badge: string;
  badgeColor: string;
}

function workflowConfig(actionType: string): TriggerConfig {
  switch (actionType) {
    case 'SUBMIT_MONTH':
      return { color: '#2563eb', bg: 'rgba(37,99,235,0.09)', icon: <SubmitIcon />, badge: 'Submitted', badgeColor: '#2563eb' };
    case 'APPROVE_MONTH':
      return { color: '#16a34a', bg: 'rgba(22,163,74,0.09)', icon: <ApproveIcon />, badge: 'Approved', badgeColor: '#16a34a' };
    case 'REJECT_MONTH':
      return { color: '#dc2626', bg: 'rgba(220,38,38,0.09)', icon: <RejectIcon />, badge: 'Rejected', badgeColor: '#dc2626' };
    case 'SUPER_APPROVE_MONTH':
      return { color: '#7c3aed', bg: 'rgba(124,58,237,0.09)', icon: <StarIcon />, badge: 'Super Approved', badgeColor: '#7c3aed' };
    case 'LOCK_MONTH':
      return { color: '#0891b2', bg: 'rgba(8,145,178,0.09)', icon: <LockIcon />, badge: 'Locked', badgeColor: '#0891b2' };
    case 'UNLOCK_MONTH':
      return { color: '#d97706', bg: 'rgba(217,119,6,0.09)', icon: <UnlockIcon />, badge: 'Unlocked', badgeColor: '#d97706' };
    case 'SEND_CHASE_EMAIL':
      return { color: '#6b7280', bg: 'rgba(107,114,128,0.09)', icon: <MailIcon />, badge: 'Chase Sent', badgeColor: '#6b7280' };
    default:
      return { color: '#6b7280', bg: 'rgba(107,114,128,0.09)', icon: <BellIcon />, badge: actionType, badgeColor: '#6b7280' };
  }
}

function orderConfig(triggerType: string): TriggerConfig {
  switch (triggerType) {
    case 'ORDER_SUBMITTED':
      return { color: '#2563eb', bg: 'rgba(37,99,235,0.09)', icon: <BoxIcon />, badge: 'Submitted', badgeColor: '#2563eb' };
    case 'LM_APPROVAL_REQUEST':
    case 'CHEMICAL_APPROVAL_REQUEST':
    case 'ESCALATED_APPROVAL_REQUEST':
    case 'CEO_APPROVAL_REQUEST':
    case 'CFO_APPROVAL_REQUEST':
      return { color: '#d97706', bg: 'rgba(217,119,6,0.09)', icon: <ClockIcon />, badge: 'Pending Approval', badgeColor: '#d97706' };
    case 'ORDER_APPROVED_TO_OM':
    case 'ORDER_SELF_APPROVED_TO_OM':
      return { color: '#16a34a', bg: 'rgba(22,163,74,0.09)', icon: <ApproveIcon />, badge: 'Approved', badgeColor: '#16a34a' };
    case 'ORDER_REJECTED':
      return { color: '#dc2626', bg: 'rgba(220,38,38,0.09)', icon: <RejectIcon />, badge: 'Rejected', badgeColor: '#dc2626' };
    case 'ORDER_QUERIED':
      return { color: '#7c3aed', bg: 'rgba(124,58,237,0.09)', icon: <QueryIcon />, badge: 'Queried', badgeColor: '#7c3aed' };
    case 'ORDER_PLACED':
      return { color: '#16a34a', bg: 'rgba(22,163,74,0.09)', icon: <CheckCircleIcon />, badge: 'Placed', badgeColor: '#16a34a' };
    default:
      return { color: '#6b7280', bg: 'rgba(107,114,128,0.09)', icon: <BoxIcon />, badge: triggerType, badgeColor: '#6b7280' };
  }
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, width: 18, height: 18 };

function SubmitIcon()      { return <svg {...svgProps}><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>; }
function ApproveIcon()     { return <svg {...svgProps}><polyline points="20 6 9 17 4 12"/></svg>; }
function RejectIcon()      { return <svg {...svgProps}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function StarIcon()        { return <svg {...svgProps}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>; }
function LockIcon()        { return <svg {...svgProps}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>; }
function UnlockIcon()      { return <svg {...svgProps}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>; }
function MailIcon()        { return <svg {...svgProps}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>; }
function BellIcon()        { return <svg {...svgProps}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>; }
function BoxIcon()         { return <svg {...svgProps}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function ClockIcon()       { return <svg {...svgProps}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function CheckCircleIcon() { return <svg {...svgProps}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>; }
function QueryIcon()       { return <svg {...svgProps}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
function EyeOffIcon()      { return <svg {...svgProps} width={14} height={14}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>; }
function RefreshIcon()     { return <svg {...svgProps} width={15} height={15}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>; }

// ─── Workflow card ────────────────────────────────────────────────────────────

function WorkflowCard({ n, onHide }: { n: WorkflowTriggerNotification; onHide: (id: string) => Promise<void> }) {
  const [hiding, setHiding] = useState(false);
  const cfg = workflowConfig(n.actionType);

  const handleHide = async () => {
    setHiding(true);
    try { await onHide(n.id); } finally { setHiding(false); }
  };

  return (
    <div style={cardStyle}>
      <div style={{ ...iconWrapStyle, background: cfg.bg, color: cfg.color }}>{cfg.icon}</div>
      <div style={cardBodyStyle}>
        <div style={cardTitleStyle}>{humaniseWorkflow(n)}</div>
        <div style={cardMetaStyle}>
          <span style={{ ...badgeStyle, background: cfg.bg, color: cfg.color }}>{cfg.badge}</span>
          {n.approverRole && <span style={metaChipStyle}>{n.approverRole}</span>}
          {n.source && <span style={metaChipStyle}>{n.source}</span>}
          {n.totalHours != null && <span style={metaChipStyle}>{n.totalHours}h</span>}
        </div>
        {n.rejectionComment && (
          <div style={commentStyle}>"{n.rejectionComment}"</div>
        )}
        {n.reason && !n.rejectionComment && (
          <div style={commentStyle}>Reason: {n.reason}</div>
        )}
      </div>
      <div style={cardRightStyle}>
        <span style={timeStyle}>{timeAgo(n.created)}</span>
        <button
          style={hideButtonStyle}
          onClick={handleHide}
          disabled={hiding}
          title="Hide this notification"
        >
          <EyeOffIcon />
          <span>Hide</span>
        </button>
      </div>
    </div>
  );
}

// ─── Order card ───────────────────────────────────────────────────────────────

function OrderCard({ n, onHide }: { n: OrderWorkflowTriggerNotification; onHide: (id: string) => Promise<void> }) {
  const [hiding, setHiding] = useState(false);
  const cfg = orderConfig(n.triggerType);

  const handleHide = async () => {
    setHiding(true);
    try { await onHide(n.id); } finally { setHiding(false); }
  };

  return (
    <div style={cardStyle}>
      <div style={{ ...iconWrapStyle, background: cfg.bg, color: cfg.color }}>{cfg.icon}</div>
      <div style={cardBodyStyle}>
        <div style={cardTitleStyle}>{humaniseOrder(n)}</div>
        <div style={cardMetaStyle}>
          <span style={{ ...badgeStyle, background: cfg.bg, color: cfg.color }}>{cfg.badge}</span>
          {n.orderRef && <span style={metaChipStyle}>{n.orderRef}</span>}
          {n.orderAmount > 0 && <span style={metaChipStyle}>£{n.orderAmount.toLocaleString()}</span>}
          {n.requesterName && <span style={metaChipStyle}>{n.requesterName}</span>}
        </div>
        {n.orderTitle && <div style={subtitleStyle}>{n.orderTitle}</div>}
        {n.note && <div style={commentStyle}>"{n.note}"</div>}
      </div>
      <div style={cardRightStyle}>
        <span style={timeStyle}>{timeAgo(n.created)}</span>
        <button
          style={hideButtonStyle}
          onClick={handleHide}
          disabled={hiding}
          title="Hide this notification"
        >
          <EyeOffIcon />
          <span>Hide</span>
        </button>
      </div>
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

function SectionHeader({ title, count, icon, color }: { title: string; count: number; icon: React.ReactNode; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `rgba(${hexToRgb(color)},0.1)`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{title}</span>
      <span style={{ background: color, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, marginLeft: 2 }}>{count}</span>
    </div>
  );
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-muted)', fontSize: 13 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={40} height={40} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.35 }}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      {message}
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

type Tab = 'workflow' | 'orders';

// ─── Main component ───────────────────────────────────────────────────────────

interface NotificationsPageProps {
  notifications: UseNotificationsResult;
}

export function NotificationsPage({ notifications }: NotificationsPageProps) {
  const { workflowItems, orderItems, isLoading, error, hideWorkflow, hideOrder, refresh, markSeen, unseenCount } = notifications;
  const [tab, setTab] = useState<Tab>('workflow');

  useEffect(() => {
    void markSeen();
  }, [markSeen]);

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={headerIconStyle}>
            <BellIcon />
          </div>
          <div>
            <h1 style={pageTitleStyle}>Notifications</h1>
            <p style={pageSubStyle}>
              {workflowItems.length + orderItems.length} active notification{(workflowItems.length + orderItems.length) !== 1 ? 's' : ''}
              {unseenCount > 0 && ` · ${unseenCount} new`}
            </p>
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => void refresh()}
          disabled={isLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshIcon />
          {isLoading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div style={tabBarStyle}>
        <button
          style={tab === 'workflow' ? activeTabStyle : tabStyle}
          onClick={() => setTab('workflow')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={15} height={15} strokeLinecap="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          Timesheet Triggers
          {workflowItems.length > 0 && (
            <span style={{ background: tab === 'workflow' ? '#2563eb' : '#e5e7eb', color: tab === 'workflow' ? '#fff' : '#374151', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, marginLeft: 4 }}>
              {workflowItems.length}
            </span>
          )}
        </button>
        <button
          style={tab === 'orders' ? activeTabStyle : tabStyle}
          onClick={() => setTab('orders')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={15} height={15} strokeLinecap="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
          </svg>
          Order Triggers
          {orderItems.length > 0 && (
            <span style={{ background: tab === 'orders' ? '#2563eb' : '#e5e7eb', color: tab === 'orders' ? '#fff' : '#374151', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, marginLeft: 4 }}>
              {orderItems.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {error && (
          <div className="err-banner" style={{ marginBottom: 16 }}>
            Failed to load notifications: {error.message}
          </div>
        )}

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <span style={{ fontSize: 13 }}>Loading notifications…</span>
          </div>
        )}

        {!isLoading && tab === 'workflow' && (
          <>
            <SectionHeader
              title="Timesheet Workflow Triggers"
              count={workflowItems.length}
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16} strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
              color="#2563eb"
            />
            {workflowItems.length === 0
              ? <EmptyState message="No timesheet notifications — all caught up!" />
              : workflowItems.map(n => (
                  <WorkflowCard key={n.id} n={n} onHide={hideWorkflow} />
                ))
            }
          </>
        )}

        {!isLoading && tab === 'orders' && (
          <>
            <SectionHeader
              title="Order Workflow Triggers"
              count={orderItems.length}
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16} strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>}
              color="#7c3aed"
            />
            {orderItems.length === 0
              ? <EmptyState message="No order notifications — all caught up!" />
              : orderItems.map(n => (
                  <OrderCard key={n.id} n={n} onHide={hideOrder} />
                ))
            }
          </>
        )}
      </div>
    </div>
  );
}

// ─── Push Notification Toasts ─────────────────────────────────────────────────

export function NotificationToastContainer({ toasts, onDismiss, onNavigate }: {
  toasts: import('../hooks/useNotifications').NotificationToast[];
  onDismiss: (id: string) => void;
  onNavigate: () => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div className="notif-toast-container">
      {toasts.map(t => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss, onNavigate }: {
  toast: import('../hooks/useNotifications').NotificationToast;
  onDismiss: (id: string) => void;
  onNavigate: () => void;
}) {
  const cfg = toast.kind === 'order'
    ? orderConfig(toast.subType)
    : workflowConfig(toast.subType);

  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 5500);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  const label = toast.kind === 'order' ? 'Order Update' : 'Timesheet Update';

  return (
    <div className="notif-toast" onClick={onNavigate} style={{ cursor: 'pointer' }}>
      {/* Coloured top stripe */}
      <div className="notif-toast-stripe" style={{ background: cfg.color }} />

      <div className="notif-toast-body">
        {/* Icon */}
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: cfg.bg, color: cfg.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {cfg.icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 600, background: cfg.bg, color: cfg.color, padding: '1px 6px', borderRadius: 99 }}>
              {cfg.badge}
            </span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
            {toast.title}
          </div>
          {toast.detail && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {toast.detail}
            </div>
          )}
        </div>

        {/* Dismiss */}
        <button
          className="notif-toast-close"
          onClick={e => { e.stopPropagation(); onDismiss(toast.id); }}
          aria-label="Dismiss"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} width={13} height={13} strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Progress bar — same colour as stripe */}
      <div className="notif-toast-progress" style={{ background: cfg.color }} />
    </div>
  );
}

// ─── Inline styles ────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  overflowY: 'auto',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 32px 20px',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--border)',
  flexShrink: 0,
};

const headerIconStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  background: 'rgba(37,99,235,0.09)',
  color: '#2563eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
  color: 'var(--text)',
  letterSpacing: '-0.01em',
};

const pageSubStyle: React.CSSProperties = {
  margin: '3px 0 0',
  fontSize: 12.5,
  color: 'var(--text-muted)',
};

const tabBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: '12px 32px 0',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--border)',
  flexShrink: 0,
};

const tabBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: '6px 6px 0 0',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  transition: 'background 0.15s, color 0.15s',
};

const tabStyle: React.CSSProperties = {
  ...tabBase,
  background: 'transparent',
  color: 'var(--text-muted)',
};

const activeTabStyle: React.CSSProperties = {
  ...tabBase,
  background: 'rgba(37,99,235,0.08)',
  color: '#2563eb',
  fontWeight: 600,
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  padding: '24px 32px',
  overflowY: 'auto',
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '14px 16px',
  marginBottom: 10,
  transition: 'box-shadow 0.15s',
};

const iconWrapStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const cardBodyStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 6,
  lineHeight: 1.4,
};

const cardMetaStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
  alignItems: 'center',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 99,
  letterSpacing: '0.03em',
};

const metaChipStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  color: 'var(--text-muted)',
  background: 'var(--surface2)',
  padding: '2px 7px',
  borderRadius: 5,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  marginTop: 5,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const commentStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  marginTop: 5,
  background: 'var(--surface2)',
  padding: '4px 8px',
  borderRadius: 5,
  borderLeft: '2px solid var(--border)',
};

const cardRightStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 8,
  flexShrink: 0,
};

const timeStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--text-dim)',
  whiteSpace: 'nowrap',
};

const hideButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 9px',
  fontSize: 11.5,
  fontWeight: 500,
  color: 'var(--text-muted)',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 5,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 0.12s, color 0.12s',
};
